import { httpClient } from '@/core/api/httpClient';
import { API_V1 } from '@/core/api/endpoints';
import { env } from '@/core/config/env';
import type {
  CompactWhiteboardSnapshotPayload,
  CreateWhiteboardPayload,
  CreatedResponse,
  GetWhiteboardUpdatesParams,
  RenameWhiteboardPayload,
  WhiteboardDto,
  WhiteboardSummaryDto,
  WhiteboardUpdatesDto,
} from '../types';
import { mockWhiteboardDetail, mockWhiteboards } from './whiteboards.mock';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

const whiteboardsMock: WhiteboardSummaryDto[] = [...mockWhiteboards];

export const whiteboardsService = {
  getByBoard: async (boardId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      return Promise.resolve({ data: whiteboardsMock.filter((w) => w.boardId === boardId) });
    }
    return httpClient.get<WhiteboardSummaryDto[]>(`${API_V1}/boards/${boardId}/whiteboards`, {
      signal,
    });
  },

  getById: async (whiteboardId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      const summary = whiteboardsMock.find((w) => w.id === whiteboardId);
      return Promise.resolve({
        data: {
          ...mockWhiteboardDetail,
          id: whiteboardId,
          name: summary?.name ?? mockWhiteboardDetail.name,
        } satisfies WhiteboardDto,
      });
    }
    return httpClient.get<WhiteboardDto>(`${API_V1}/whiteboards/${whiteboardId}`, { signal });
  },

  /**
   * Fetches the Yjs updates recorded after `afterSeq`, ascending.
   *
   * @remarks
   * Not wrapped in a query hook on purpose — updates are applied once to a mutable `Y.Doc`
   * and must never be replayed from cache.
   */
  getUpdates: async (
    whiteboardId: string,
    params: GetWhiteboardUpdatesParams,
    signal?: AbortSignal,
  ) => {
    if (USE_MOCK) {
      return Promise.resolve({ data: { items: [], lastSeq: 0 } satisfies WhiteboardUpdatesDto });
    }
    return httpClient.get<WhiteboardUpdatesDto>(`${API_V1}/whiteboards/${whiteboardId}/updates`, {
      params,
      signal,
    });
  },

  create: async (boardId: string, data: CreateWhiteboardPayload) => {
    if (USE_MOCK) {
      const created: WhiteboardSummaryDto = {
        id: `wb-${Date.now()}`,
        boardId,
        name: data.name,
        createdById: 'user-1',
        createdBy: 'Guillermo Sandoval',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        activeEditorCount: 0,
      };
      whiteboardsMock.push(created);
      return Promise.resolve({ data: { id: created.id } satisfies CreatedResponse });
    }
    return httpClient.post<CreateWhiteboardPayload, CreatedResponse>(
      `${API_V1}/boards/${boardId}/whiteboards`,
      data,
    );
  },

  rename: async (whiteboardId: string, data: RenameWhiteboardPayload) => {
    if (USE_MOCK) {
      const target = whiteboardsMock.find((w) => w.id === whiteboardId);
      if (target) {
        target.name = data.name;
        target.updatedAt = new Date().toISOString();
      }
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.patch<RenameWhiteboardPayload, void>(
      `${API_V1}/whiteboards/${whiteboardId}`,
      data,
    );
  },

  delete: async (whiteboardId: string) => {
    if (USE_MOCK) {
      const index = whiteboardsMock.findIndex((w) => w.id === whiteboardId);
      if (index >= 0) whiteboardsMock.splice(index, 1);
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.delete<void>(`${API_V1}/whiteboards/${whiteboardId}`);
  },

  /**
   * Replaces the stored snapshot with a locally merged one and lets the server drop the
   * folded updates.
   *
   * @remarks
   * Sent with `skipGlobalError` because a `409 Whiteboard.SnapshotStale` is expected whenever
   * two clients answer the same compaction request — it means another client won the race,
   * which is not something the user should be told about. The rejection still propagates.
   */
  compactSnapshot: async (whiteboardId: string, data: CompactWhiteboardSnapshotPayload) => {
    if (USE_MOCK) {
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.put<CompactWhiteboardSnapshotPayload, void>(
      `${API_V1}/whiteboards/${whiteboardId}/snapshot`,
      data,
      { skipGlobalError: true },
    );
  },
};
